import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface BlogPostRow {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  author: string;
  published_at: string;
}

export default function LatestSellerAdvice() {
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, cover_image, author, published_at')
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .limit(3);

        setPosts((data as BlogPostRow[]) || []);
      } catch {
        // Silently fail - blog section is optional
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="text-primary-500 font-body text-sm tracking-widest uppercase mb-3">Latest Advice</p>
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-4">
            Seller Tips & Insights
          </h2>
          <p className="text-foreground-600 font-body text-sm max-w-[500px] mx-auto">
            Helpful articles to guide you through selling, preparing, and understanding your property's value.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-foreground-400 font-body text-sm">Check back soon for new articles.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/60 transition-all duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  {post.cover_image ? (
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-background-200 flex items-center justify-center">
                      <i className="ri-article-line text-foreground-300 text-2xl" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-foreground-900 text-base mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-foreground-500 font-body text-xs line-clamp-2 mb-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-foreground-400 font-body text-xs">
                    <span>{formatDate(post.published_at)}</span>
                    <span>·</span>
                    <span className="text-primary-500 font-medium flex items-center gap-1">
                      Read More <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-background-100 text-foreground-700 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-background-200 transition-all"
          >
            View All Seller Resources <i className="ri-arrow-right-line text-base" />
          </Link>
        </div>
      </div>
    </section>
  );
}